/**
 * Which seller sent this browser, remembered from the first visit.
 *
 * A seller's link is the ordinary sign-up page with ?ref=<code> on it, for
 * example soup.red/#/register?ref=ana. The code is saved on the first page
 * that carries it and kept for 90 days, so an account created in the same
 * browser later still counts - even when the visitor comes back through a
 * plain link with no ?ref on it.
 *
 * The first seller wins: a later link from somebody else does not replace the
 * one already saved. The server applies the same rule (one account, one
 * seller) and checks the code against the sellers that actually exist, so
 * nothing stored here has to be trusted.
 */

const KEY = "soup_ref";
const DAYS = 90;
const CODE = /^[a-z0-9]{1,32}$/;

function refFrom(query) {
  try {
    const value = new URLSearchParams(query || "").get("ref");
    const code = (value || "").trim().toLowerCase();
    return CODE.test(code) ? code : "";
  } catch (e) {
    return "";
  }
}

/** The saved seller code, or "" when there is none or it has expired. */
export function readReferral() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!saved || !CODE.test(saved.code || "")) return "";
    if (Date.now() - (saved.at || 0) > DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(KEY);
      return "";
    }
    return saved.code;
  } catch (e) {
    return "";
  }
}

/**
 * Save the ?ref on the current address, unless a seller is already saved.
 *
 * With a HashRouter the ref can sit in two places, and both are read:
 *   soup.red/?ref=ana#/register     (before the hash)
 *   soup.red/#/register?ref=ana     (inside it)
 */
export function captureReferral() {
  const hash = window.location.hash || "";
  const code =
    refFrom(window.location.search) ||
    refFrom(hash.includes("?") ? hash.slice(hash.indexOf("?")) : "");

  const saved = readReferral();
  if (!code || saved) return saved;

  try {
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch (e) {
    // Private browsing or a full storage: the server-side cookie set by the
    // /r/<code>/ link is the fallback, and nothing else depends on this.
  }
  return code;
}
